<template>
  <!-- Device groups -->
  <TriggerComponentDeviceGroups class="pa-0" :deviceGroupsChosen="deviceGroupsChosen"/>
  <!-- Model Path Matcher -->
  <TriggerComponentModelPathMatcher class="pa-0" :policy="policy" @deviceSlotModelTag="evaluateTagSelected" @modelPathMatcherMode="pathMatcherModeChanged"/>
  <!-- Mode -->
   <v-row class="my-0 pt-4 pb-0">
    <v-col cols="3" class="py-1 pr-0">{{ $t("mode") }}: </v-col>
    <v-col cols="9" class="py-1 pr-0">
      <div class="radio-container mx-4" @click="togglePolicyMask(TriggerPolicyLevelMaskEnum.ON_ENTER)">
        <v-icon 
          :color="policy.mode == TriggerPolicyLevelMaskEnum.ON_ENTER ? 'var(--color-highlight)' : 'var(--color-g4)'"
          class="ecc" 
          style="font-size:22px">{{policy.mode == TriggerPolicyLevelMaskEnum.ON_ENTER  ? 'ecc:ecc-E-RadioON' : 'ecc:ecc-E-RadioOFF'}}</v-icon>
        <div class="radio-text">{{ $t('onEnter') }}</div>
      </div>
      <div class="radio-container" @click="togglePolicyMask(TriggerPolicyLevelMaskEnum.ON_EXIT)">
        <v-icon 
          :color="policy.mode == TriggerPolicyLevelMaskEnum.ON_EXIT ? 'var(--color-highlight)' : 'var(--color-g4)'"
          class="ecc" 
          style="font-size:22px">{{policy.mode == TriggerPolicyLevelMaskEnum.ON_EXIT ? 'ecc:ecc-E-RadioON' : 'ecc:ecc-E-RadioOFF'}}</v-icon>
        <div class="radio-text">{{ $t('onExit') }}</div>
      </div>
    </v-col>
  </v-row>
  <!-- Match type -->
  <v-row class="my-0 pt-4 pb-0">
    <v-col cols="3" class="py-1 pr-0">{{ $t("matchType") }}: </v-col>
    <v-col cols="9" class="py-1 pr-0">
      <div class="radio-container mx-4">
        <v-icon 
          :color="matchType == 'numeric' ? 'var(--color-highlight)' : 'var(--color-g4)'"
          :disabled="forceMatchType"
          class="ecc" 
          @click="togglePolicyMatchType('numeric')"
          style="font-size:22px">{{matchType == 'numeric'  ? 'ecc:ecc-E-RadioON' : 'ecc:ecc-E-RadioOFF'}}</v-icon>
        <div class="radio-text">{{ $t('numeric') }}</div>
      </div>
      <div class="radio-container mr-4">
        <v-icon 
          :color="matchType == 'string' ? 'var(--color-highlight)' : 'var(--color-g4)'"
          :disabled="forceMatchType"
          class="ecc" 
          @click="togglePolicyMatchType('string')"
          style="font-size:22px">{{matchType == 'string' ? 'ecc:ecc-E-RadioON' : 'ecc:ecc-E-RadioOFF'}}</v-icon>
        <div class="radio-text">{{ $t('text') }}</div>
      </div>
      <div class="radio-container">
        <v-icon 
          :color="matchType == 'boolean' ? 'var(--color-highlight)' : 'var(--color-g4)'"
          :disabled="forceMatchType"
          class="ecc" 
          @click="togglePolicyMatchType('boolean')"
          style="font-size:22px">{{matchType == 'boolean' ? 'ecc:ecc-E-RadioON' : 'ecc:ecc-E-RadioOFF'}}</v-icon>
        <div class="radio-text">{{ $t('boolean') }}</div>
      </div>
    </v-col>
  </v-row>
  <!-- min max -->
  <v-row v-if="matchType === 'numeric'" class="my-1 mb-2">
    <v-col class="py-0">
      <v-row class="pa-0 ma-0">
        <v-btn :class="{ 'btn-base': true, 'btn-left': true, 'btn-active': !policy.minOrEqual, 'btn-unactive': policy.minOrEqual }" @click="setMinOrEqual(false)" tile variant="plain">></v-btn>
        <v-btn :class="{ 'btn-base': true, 'btn-right': true, 'btn-active': policy.minOrEqual, 'btn-unactive': !policy.minOrEqual }" @click="setMinOrEqual(true)" tile variant="plain">≥</v-btn>
        <v-text-field v-model="policy.min" type="number" :label="$t('min')" class="half pr-2 pl-4" data-qa="prop-control-grid-cols" hide-details> </v-text-field>
      </v-row>
      <v-row class="pa-0 ma-0">
        <v-btn :class="{ 'btn-base': true, 'btn-left': true, 'btn-active': !policy.maxOrEqual, 'btn-unactive': policy.maxOrEqual }" @click="setMaxOrEqual(false)" tile variant="plain"><</v-btn>
        <v-btn :class="{ 'btn-base': true, 'btn-right': true, 'btn-active': policy.maxOrEqual, 'btn-unactive': !policy.maxOrEqual }" @click="setMaxOrEqual(true)" tile variant="plain">≤</v-btn>
        <v-text-field v-model="policy.max" type="number" :label="$t('max')" class="half pr-2 pl-4" data-qa="prop-control-grid-cols" hide-details> </v-text-field>
      </v-row>
      <v-row class="pa-0 ma-0" v-if="Number(policy.min) > Number(policy.max)">
        <div style="color: var(--color-alert);">{{ $t("minGreaterMax") }}</div>
      </v-row>
    </v-col>
  </v-row>
  <!-- match string -->
  <v-row v-if="matchType === 'string'" class="my-1">
    <v-col class="py-0">
      <v-text-field
        v-model="policy.level"
        type="text"
        :label="$t('match')"
        data-qa="trigger-policy-level-min"
      >
      </v-text-field>
    </v-col>
  </v-row>
  <!-- match boolean -->
  <v-row v-if="matchType === 'boolean'" class="my-0 pt-4 pb-0">
    <v-col cols="3" class="py-1 pr-0">{{ $t("boolean") }}: </v-col>
    <v-col cols="9" class="py-1 pr-0">
      <div class="radio-container mx-4" @click="togglePolicyLevelBool(true)">
        <v-icon 
          :color="policy.levelBool == true ? 'var(--color-highlight)' : 'var(--color-g4)'"
          class="ecc" 
          style="font-size:22px">{{ policy.levelBool == true ? 'ecc:ecc-E-RadioON' : 'ecc:ecc-E-RadioOFF'}}</v-icon>
        <div class="radio-text">{{ $t('TRUE') }}</div>
      </div>
      <div class="radio-container mr-4" @click="togglePolicyLevelBool(false)">
        <v-icon 
          :color="policy.levelBool == false ? 'var(--color-highlight)' : 'var(--color-g4)'"
          class="ecc" 
          style="font-size:22px">{{ policy.levelBool == false ? 'ecc:ecc-E-RadioON' : 'ecc:ecc-E-RadioOFF'}}</v-icon>
        <div class="radio-text">{{ $t('FALSE') }}</div>
      </div>
    </v-col>
  </v-row>
  <!-- skip n changes -->
  <v-row class="my-1">
    <v-col class="py-0">
      <v-text-field
        v-model="policy.skipFirstChanges"
        :min="0"
        :step="0"
        type="number"
        :label="$t('setNFirstChangedToSkip')"
        data-qa="prop-control-grid-time"
        hide-details>
      </v-text-field>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import TriggerComponentModelPathMatcher from "./TriggerComponentModelPathMatcher.vue";
import TriggerComponentDeviceGroups from "./TriggerComponentDeviceGroups.vue";
import { ModelTypeEnum, modelTypeToInputPropControlType, TriggerPolicyLevelMaskEnum, TriggerPolicyModelPathMatcherEnum, TriggerPolicyOnLevel } from "./type.util";

export default defineComponent({
  name: "TriggerPolicyAnalogBandComponent",
  components: {
    TriggerComponentModelPathMatcher,
    TriggerComponentDeviceGroups,
  },
  data() {
    return {
      TriggerPolicyLevelMaskEnum: TriggerPolicyLevelMaskEnum,
      matchType: 'numeric',
      forceMatchType: false,
      tagSelected: ModelTypeEnum.INTEGER,
    };
  },
  props: {
    policy: {
      type: Object as () => TriggerPolicyOnLevel,
      default: null,
    },
    deviceGroupsChosen: {
      type: Array,
      default: [],
    },
  },
  methods: {
    initPolicy(): void {
      if (this.policy.level) {
        this.matchType = 'string';
      } else if (this.policy.levelBool !== undefined) {
        this.matchType = 'boolean';
      } else {
        this.matchType = 'numeric';
      }
    },
    setMinOrEqual(value: boolean): void {
      this.policy.minOrEqual = value;
    },
    setMaxOrEqual(value: boolean): void {
      this.policy.maxOrEqual = value;
    },
    togglePolicyMask(mask: TriggerPolicyLevelMaskEnum): void {
      this.policy.mode = mask;
    },
    togglePolicyMatchType(type: string): void {
      this.matchType = type;
    },
    togglePolicyLevelBool(value: boolean): void {
      this.policy.levelBool = value;
    },
    evaluateTagSelected(value: ModelTypeEnum) {
      this.tagSelected = value;
      this.matchType = modelTypeToInputPropControlType(this.tagSelected) 
      this.forceMatchType = this.tagSelected != null;
    },
    pathMatcherModeChanged(value) {
      if (value === TriggerPolicyModelPathMatcherEnum.ADVANCED) {
        this.forceMatchType = false;
      } else {
        this.evaluateTagSelected(this.tagSelected);
      }
    }
  },
  watch: {
    matchType: function (value): void {
      if (value == 'numeric') {
        this.policy.level = "";
        this.policy.levelBool = undefined;
      } else if (value == 'string') {
        this.policy.levelBool = undefined;
        this.policy.minOrEqual = true;
        this.policy.min = NaN;
        this.policy.maxOrEqual = true;
        this.policy.max = NaN;
      } else {
        this.policy.level = "";
        this.policy.minOrEqual = true;
        this.policy.min = NaN;
        this.policy.maxOrEqual = true;
        this.policy.max = NaN;
      }
    },
  },
  mounted() {
    this.initPolicy();
  },
});
</script>

<style scoped>
.header {
  font-weight: bold;
}
.radio-container {
  display: inline-flex;
  cursor: pointer;
}

.radio-text{
  margin: auto;
}
.radio-label {
  color: var(--color-g4);
}
.half {
  width: 50%;
}
.v-theme--light.v-btn.btn-base {
  font-size: 20px;
  font-weight: normal;
  min-height: 42px;
  padding: 0;
  letter-spacing: normal;
}
.v-theme--light.v-btn.btn-active {
  background: var(--color-g2);
}
.v-theme--light.v-btn.btn-unactive {
  background: var(--color-g1);
}
.btn-left {
  border-bottom-left-radius: 5px !important;
  border-top-left-radius: 5px !important;
}
.btn-right {
  border-bottom-right-radius: 5px !important;
  border-top-right-radius: 5px !important;
}
</style>
